import { Component, OnDestroy, OnInit } from '@angular/core'
import { ActivatedRoute, NavigationEnd, NavigationStart, Router } from '@angular/router'
import { combineLatest, from, merge, Observable, of, ReplaySubject, Subject, Subscription } from 'rxjs'
import {
  debounceTime,
  filter,
  first,
  map,
  mergeMap,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom
} from 'rxjs/operators'
import { NavStateContainer } from '../../models/nav-state-container'
import { StateToken } from '../../models/state-token.model'
import { JmlNavAdapterService, TokenSources } from '../../services/jml-nav-adapter.service'
import * as _ from 'lodash'
import TokenUtils from '../../utils/token.utils'

/**
 * This is the base class/-component for components with state managed via token.
 *
 * responsibilities:
 *
 *  start:
 *
 * - check url for token
 * - if there is no url-token, generate url-token
 * - if need, generate/initialize component-token
 * - else check store for state with full token
 * - if state was found, load state
 * - cast state to derived state
 *
 *  after state changes:
 *
 * - save state to store
 *
 * note:
 * - url-token is the part of the token that is visible in the url.
 * - component-token is used for components within the component-structure
 *   to identify there part of the state with the same url-token.
 *
 *
 * Todo:
 * - after all default use-cases are determined, make as much properties and methods
 *   private as possible to reduce noise in derived components.
 *
 * improvement-ideas
 *
 * Would that state structure be better usable with a local service injected into the base component? (route-target)
 * That would components allow to inherit from other components.
 *
 */
@Component({
  selector: 'jml-lib-base-component',
  // this component will never be displayed, so no template
  template: ``,
  styles: []
})
export class BaseNavStateComponent<T> implements OnInit, OnDestroy {
  /** fires, if no state for component was found. Hook to initialize component with new state. */
  // here no type is needed. is there a better way to design that than with any?
  initWithNewState$: Observable<any>
  /** fires, if state for component was found. Hook to initialize component with existing state. */
  initWithExistingState$: Observable<T>
  /** fires when either state was found or no state exists - this enables consumers to easily handle both cases within one stream. */
  initWithAnyState$: Observable<T>
  /** determines, whether there a state in the store for this component.
   * (different from the initial state, which usually is not saved in the store.) */
  storeHasSavedStateForToken$: Observable<boolean>
  /** custom part of the site state */
  compState$: Observable<T>
  stopSubscriptions$: Observable<void>

  /** the current state of the site state object */
  private navStateContainer$: Observable<NavStateContainer<T>>
  /** the component part of the token */
  private compIdentifier$: Observable<string>
  /** the full token to use with the ngrx-store */
  // private token$: Observable<string>;
  /** the part of the token that is contained within the url */
  urlToken$: Observable<string>
  /** Determines, whether the current url token was part of the route, when this component was loaded,
   *  or if the token was generated afterwards.
   */
  private urlTokenGeneratedFresh$: Observable<boolean>
  /** buffer for saving operations to use debounceTime operator */
  private saveDebouncer$: Observable<T>
  /** navigation events of the naviation to this component */
  private lastNavEvents$: Observable<{ navStart: NavigationStart; navEnd: NavigationEnd }>
  /** Determines, if the navigation to this component was a browser-navigation (via forward/backward button of the browser) */
  private lastNavWasBrowserNav$: Observable<boolean>
  /** Determines, if the navigation to this component was a browser-navigation without having an already existing token in the url. */
  private lastNavWasBrowserNavWithLostToken$: Observable<boolean>
  private tokenObj$: Observable<StateToken>

  /** Determines, whether the component is the top level component for a given route and is therefore
   * responsible for creating a token, if non is provided in the route.
   */
  private isRouteComponent = false
  /** time delay where no state change happens before an actual saving of a state takes place. */
  private debounceTime = 500 // ms

  // subjects
  private $navStateContainer: ReplaySubject<NavStateContainer<T>>
  private $compIdentifier: ReplaySubject<string>
  private $storeHasSavedStateForToken: ReplaySubject<boolean>
  private $urlTokenCheckResult: ReplaySubject<{ source: TokenSources; token: string }>
  private $saveDebouncer: Subject<T>
  private $stopSubscriptions: Subject<void>

  private _subscriptions: Subscription[] = []

  private _initialStateConstructor: () => T
  private _lastSavedState: T

  constructor(
    private _jmlNav: JmlNavAdapterService,
    private _route: ActivatedRoute,
    private _router: Router
  ) {
    // initialize subjects and observables
    this.$navStateContainer = new ReplaySubject<NavStateContainer<T>>(1)
    this.navStateContainer$ = this.$navStateContainer.asObservable()
    this.$compIdentifier = new ReplaySubject<string>(1)
    this.compIdentifier$ = this.$compIdentifier.asObservable()
    this.$storeHasSavedStateForToken = new ReplaySubject<boolean>(1)
    this.storeHasSavedStateForToken$ = this.$storeHasSavedStateForToken.asObservable()
    this.$urlTokenCheckResult = new ReplaySubject<{ source: TokenSources; token: string }>(1)
    this.$saveDebouncer = new Subject<T>()
    this.saveDebouncer$ = this.$saveDebouncer.asObservable()
    this.$stopSubscriptions = new Subject<void>()
    this.stopSubscriptions$ = this.$stopSubscriptions.asObservable()
  }

  ngOnInit() {
    this.urlToken$ = this.$urlTokenCheckResult.pipe(map((result) => result.token))
    this.urlTokenGeneratedFresh$ = this.$urlTokenCheckResult.pipe(map((result) => result.source === 'generated'))

    this.lastNavEvents$ = this._jmlNav.getLastNavigation$()

    // this.lastNavEvents$
    //   .pipe(
    //     takeUntil(this.$stopSubscriptions)
    //   ).subscribe(events => {
    //     console.log('nav events: ', events);
    //   });

    // prevents that the token gets deleted if user clicks on the same item in the navbar again
    this.lastNavEvents$
      .pipe(
        takeUntil(this.$stopSubscriptions),
        // withLatestFrom(of(this.isRouteComponent)),
        // filter(([, isRouteComp]) => isRouteComp),
        // map(([events]) => events),
        filter((_) => this.isRouteComponent),
        filter((events) => events.navStart?.navigationTrigger === 'imperative'),
        filter((events) => !events.navStart?.url?.includes(TokenUtils.TOKEN_KEY)),
        filter((events) => events.navEnd?.url?.includes(TokenUtils.TOKEN_KEY)),
        // only set the token new if the url doesn't change (aka navigation to another component)
        filter((events) => events.navStart?.url.split('?')[0] === events.navEnd.url.split('?')[0]),
        // tap(events => console.log('nav events after filter: ', events)),
        // map(events => events.navEnd.url.split('token=')[1]),
        map((events) => events.navEnd?.url.split(`${TokenUtils.TOKEN_KEY}=`)[1]),
        tap((token) => this._jmlNav.writeTokenToUrl(this._router, token))
        // tap(_ => console.log('after writing to url')),
      )
      .subscribe()
    // this.subscriptions.push(
    //   this.lastNavEvents$
    //   .pipe(
    //     filter(_ => this.isRouteComponent),
    //     filter(events => events.navStart.navigationTrigger === 'imperative'),
    //     filter(events => !events.navStart.url?.includes('token')),
    //     filter(events => events.navEnd.url?.includes('token')),
    //     // only set the token new if the url doesn't change (aka navigation to another component)
    //     filter(events => events.navStart.url.split('?')[0] === events.navEnd.url.split('?')[0]),
    //     // tap(events => console.log('nav events after filter: ', events)),
    //     map(events => events.navEnd.url.split('token=')[1]),
    //     tap(token => this._jmlNav.writeTokenToUrl(this._router, token)),
    //     // tap(_ => console.log('after writing to url')),
    //   ).subscribe()
    // )

    this.lastNavWasBrowserNav$ = this.lastNavEvents$.pipe(
      map(({ navStart }) => !!navStart && navStart.navigationTrigger === 'popstate')
    )

    const hasToken$ = this._jmlNav.hasToken$(this._route).pipe(
      take(1) // only the first state of the url is relevant for this boolean.
    )

    this.lastNavWasBrowserNavWithLostToken$ = this.lastNavWasBrowserNav$.pipe(
      withLatestFrom(hasToken$),
      map(([wasBrowserNav, hasToken]) => wasBrowserNav && !hasToken)
    )

    this.tokenObj$ = combineLatest([this.urlToken$, this.compIdentifier$]).pipe(
      map(([urlToken, compIdentifier]) => {
        const result = StateToken.generateTokenObs(urlToken, compIdentifier)
        // console.log('result of combineLatest for tokenObj: ', result);
        return result
      })
    )

    // is this a top-level component (referenced for this route)
    if (this.isRouteComponent) {
      // if this is forward navigation via the browser, there might be state but no token
      this.lastNavWasBrowserNavWithLostToken$
        .pipe(
          take(1),
          filter((wasBrowserNavWithLostToken) => wasBrowserNavWithLostToken),
          switchMap(() => this.lastNavEvents$),
          switchMap(({ navStart }) =>
            this._jmlNav.stateForBrowserNavExists$(navStart).pipe(
              switchMap((stateExists) => {
                if (stateExists) {
                  return this._jmlNav.getStateForRouteEventId<T>(navStart).pipe(
                    tap((state) => {
                      // write token to url
                      this._jmlNav.writeTokenToUrl(this._router, state.tokenObj.urlToken)

                      // set state
                      this.$navStateContainer.next(state) // this could be ignored here to have it work the same with all paths
                      // set flags
                      this.$urlTokenCheckResult.next({
                        source: 'found' as TokenSources,
                        token: state.tokenObj.urlToken
                      })
                    })
                  )
                }
              })
            )
          )
        )
        .subscribe()

      // if this is either no browser navigation or the token is provided

      this.lastNavWasBrowserNavWithLostToken$
        .pipe(
          take(1),
          filter((wasBrowserNavWithLostToken) => !wasBrowserNavWithLostToken),
          switchMap(() => this._jmlNav.readOrInitToken$(this._route, this._router)),
          take(1),
          tap((tokenCheckResult) => this.$urlTokenCheckResult.next(tokenCheckResult))
        )
        .subscribe()
    } else {
      // wait, until token is available.
      this._jmlNav
        .readToken$(this._route)
        .pipe(
          take(1),
          map((token) => ({ source: 'found' as TokenSources, token })),
          tap((result) => this.$urlTokenCheckResult.next(result))
        )
        .subscribe()
    }

    // get the (inner) custom state from the full site state
    this.compState$ = this.storeHasSavedStateForToken$.pipe(
      take(1),
      filter((hasState) => hasState), // take only initially loaded, not newly saved state.
      switchMap(() => this.navStateContainer$),
      take(1),
      // tap(stateContainer => console.log('stateContainer: ', stateContainer)),
      map((navStateContainer) =>
        !!navStateContainer?.baseState
          ? (navStateContainer.baseState as T)
          : !!navStateContainer?._compState
            ? (navStateContainer._compState as T)
            : undefined
      ),
      // tap(stateContainer => console.log('stateContainer2: ', stateContainer)),
      map((state) => (!!state ? _.cloneDeep(state) : state))
      // tap(stateContainer => console.log('stateContainer3: ', stateContainer)),
    )

    // read stored state (if there is stored state)
    combineLatest([this.urlTokenGeneratedFresh$, this.tokenObj$, this.storeHasSavedStateForToken$])
      .pipe(
        filter(
          ([urlTokenGeneratedFresh, , storeHasSavedStateForToken]) =>
            !urlTokenGeneratedFresh && storeHasSavedStateForToken
        ),
        take(1),
        map(([, token]) => token.token),
        switchMap((token) => this._jmlNav.loadStateForToken$<T>(token)),
        tap((stateContainer) => this.$navStateContainer.next(stateContainer))
      )
      .subscribe()

    // check if the store has state for the combined token
    this.tokenObj$
      .pipe(
        take(1),
        switchMap((token) => this._jmlNav.hasStateForToken$(token.token)),
        tap((hasStateForToken) => this.$storeHasSavedStateForToken.next(hasStateForToken))
      )
      .subscribe()

    // the following two observables are for easier state access on the consumer side
    this.initWithExistingState$ = this.storeHasSavedStateForToken$.pipe(
      take(1),
      filter((storeHasToken) => storeHasToken),
      switchMap(() => this.compState$),
      // tap(state => console.log('initWithExistingState$: ', state)),
      tap((state) => {
        this._lastSavedState = state
        // console.log('update: resetting _lastSavedState: ', this._lastSavedState);
      })
    )

    this.initWithNewState$ = this.storeHasSavedStateForToken$.pipe(
      take(1),
      filter((storeHasToken) => !storeHasToken)
    )

    this.initWithAnyState$ = merge([
      this.initWithExistingState$,
      this.initWithNewState$.pipe(map((state) => <T>state))
    ]).pipe(mergeMap((v) => v))

    // debounce saving new state to avoid generation of new states with every key-stroke
    this._subscriptions.push(
      this.saveDebouncer$
        .pipe(
          debounceTime(this.debounceTime),
          withLatestFrom(this.tokenObj$),
          withLatestFrom(this.lastNavEvents$),
          map(
            ([[customState, tokenObj], navState]) =>
              [customState, tokenObj, navState.navEnd] as [T, StateToken, NavigationEnd]
          ),
          map(([customState, tokenObj, navState]) => this.generateStateContainer(tokenObj, navState.id, customState)),
          tap((siteState) => this._jmlNav.saveState<T>(siteState))
        )
        .subscribe()
    )
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach((sub) => sub.unsubscribe())
    this.$compIdentifier.complete()
    this.$navStateContainer.complete()
    this.$saveDebouncer.complete()
    this.$storeHasSavedStateForToken.complete()
    this.$urlTokenCheckResult.complete()
    console.log(' this.$stopSubscriptions.complete()')
    this.$stopSubscriptions.next()
    this.$stopSubscriptions.complete()
  }

  /**
   * Initializes the state management settings.
   *
   * @param [isRouteComponent=false] Determines, wheter this component is a route-/top-level component, linked to this route.
   * If set to true, this component will check the url for an url-token and generate a new one, if it doesn't find one.
   * @param [hasCustomComponentIdentifier=false] Determines, whether to wait  with the search for existing state until a
   * component token is provided (with setComponentToken()). The component token helps nested components to find their custom state
   * for a given url token, that is shared in the component tree for a given url.
   * @param [stateSavingDebounceTime=500] time buffer for actual state saving. (Useful for instance with text input to not create new state for each key-stroke)
   */
  initNavStateSettings(
    isRouteComponent: boolean = false,
    hasCustomComponentIdentifier: boolean = false,
    stateSavingDebounceTime: number = 500
  ): void {
    this.isRouteComponent = isRouteComponent
    this.debounceTime = stateSavingDebounceTime
    if (!hasCustomComponentIdentifier) {
      this.setComponentIdentifier('')
    }
  }
  /** Sets the component part of the token. By doing this, multiple nested components can manage their separate state with the same url-token.
   * Note: !!! This has to be called before super.ngOnInit() !!!
   * Top-level components (container-components, that are the target of a route) don't need to do this, but they are free to do it anyway.
   * @param [uniqueComponentIdentifier] literal that will be used as an identifier for this component.
   */
  setComponentIdentifier(uniqueComponentIdentifier: string): void {
    this.$compIdentifier.next(uniqueComponentIdentifier)
  }

  /** Saves current component state to the store.
   * WARNING: should only be used when the state is complete. Otherwise use updateState().
   */
  saveState(customState: T, cloneState = true): void {
    const newState = cloneState ? _.cloneDeep(customState) : customState
    this.$saveDebouncer.next(newState)
    this._lastSavedState = newState
  }

  /** Is used to update existing state. Useful, if a call to save the state only has partial information about the state.
   * With this method a callback can be provided, which, given the last complete state returns an updated state to save.
   * The provided state is a clone of the current state, so it can be manipulated without side-effects.
   * */
  updateState(updateCallback: (oldState: T) => T): void {
    // console.log('updateState - writing new state3. old state: ', this._lastSavedState);

    const newState = updateCallback(
      !!this._lastSavedState ? _.cloneDeep(this._lastSavedState) : this._initialStateConstructor()
    )
    this.saveState(newState, false)
  }

  /** Removes existing state for this component (if state exists. if not, nothing happens) */
  removeState(): void {
    this.tokenObj$
      .pipe(
        first(),
        tap((token) => this._jmlNav.removeState(token.token))
      )
      .subscribe()
    this._lastSavedState = null
  }

  public setInitialStateGenerator(generator: () => T): void {
    this._initialStateConstructor = generator
  }

  /** Generate a new NavStateContainer */
  private generateStateContainer(tokenObj: StateToken, navId: number, customState?: T): NavStateContainer<T> {
    const siteState = new NavStateContainer<T>()

    siteState.tokenObj = tokenObj
    siteState._compState = customState
    siteState.url = this._route.snapshot.url.join('')
    siteState.navId = navId

    return siteState
  }
}
